<template>
  <v-container fluid class="py-0">
    <v-layout width="100%">
      <v-row>
        <v-card width="100%" height="100%" flat>
          <v-container fluid class="py-0 fill-height">
            <v-layout>
              <v-row class="py-0 fill-height">
                <v-card width="50px" flat tile style="z-index: 1; box-shadow: 3px 0 3px -2px rgba(0, 0, 0, 0.1);">
                  <v-row align="center" class="fill-height">
                    <v-col>
                      <v-row justify="center" align="center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon small v-on="on" class="pt-1">schedule</v-icon>
                          </template>
                          {{ $t('workSchedules.workedTime') }}
                        </v-tooltip>
                      </v-row>
                      <v-row justify="center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon small v-on="on" class="pt-1">calendar_today</v-icon>
                          </template>
                          {{ $t('workSchedules.scheduledTime') }}
                        </v-tooltip>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card>
                <v-divider vertical/>
                <v-card style="width: calc(100% - 102px); overflow: auto"
                        class="px-2"
                        :ref="getCardRef()"
                        flat
                        tile
                >
                  <v-row class="ma-0"
                         :style="{ width: `${100 * magnificationFactor * timePair.spanningDays}% !important` }"
                  >
                    <v-col cols="12" class="py-0">
                      <v-row>
                        <div style="height: 1px; width:100%; background-color: darkgrey"/>
                      </v-row>
                      <v-row align="center">
                        <v-col v-for="(marker, i) in getTimeMarkers(timePair.spanningDays)"
                               v-bind:key="i"
                               class="pb-1 pt-0 px-0"
                        >
                          <div style="height: 5px; width: 2px; background-color: darkgrey"></div>
                          <div class="caption">{{ marker }}</div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <WorkScheduleBar :segments="timePair.workSchedule.actual.segments"
                                         :time-pair-id="timePair.id"
                                         :spanning-days="timePair.spanningDays"
                                         class="mb-2"
                        />
                      </v-row>
                      <v-row>
                        <WorkScheduleBar :segments="timePair.workSchedule.expected.segments"
                                         :time-pair-id="timePair.id"
                                         :spanning-days="timePair.spanningDays"
                                         faded
                                         class="mb-4"
                        />
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card>
                <v-divider vertical/>
                <v-card width="50px" flat tile style="z-index: 1; box-shadow: -3px 0 3px -2px rgba(0, 0, 0, 0.1);">
                  <v-row class="fill-height" align="center" justify="center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn icon @click="handleZoomClick()" v-on="on">
                          <v-icon>{{ isZoomed() ? 'zoom_out' : 'zoom_in' }}</v-icon>
                        </v-btn>
                      </template>
                      {{ isZoomed() ? $t('actions.zoomOut') : $t('actions.zoomIn') }}
                    </v-tooltip>
                  </v-row>
                </v-card>
              </v-row>
            </v-layout>
          </v-container>
        </v-card>
      </v-row>
    </v-layout>
  </v-container>
</template>

<script>
import addHours from 'date-fns/addHours';
import startOfDay from 'date-fns/startOfDay';
import format from 'date-fns/format';
import isBefore from 'date-fns/isBefore';
import differenceInMinutes from 'date-fns/differenceInMinutes';

import times from 'lodash/times';
import filter from 'lodash/filter';

import Vue from 'vue';
import DateTimeUtil from '../../util/date-time-util';
import WorkScheduleBar from './WorkScheduleBar.vue';
import constants from '../../constants';

export default {
  name: 'WorkScheduleCard',
  components: { WorkScheduleBar },

  props: {
    timePair: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      segmentBoundaryTypes: {
        ACTUAL_START: 'ACTUAL_START',
        ACTUAL_STOP: 'ACTUAL_STOP',
        EXPECTED_START: 'EXPECTED_START',
        EXPECTED_STOP: 'EXPECTED_STOP',
        ACTUAL_BREAK_START: 'ACTUAL_START',
        ACTUAL_BREAK_STOP: 'ACTUAL_STOP',
        EXPECTED_BREAK_START: 'EXPECTED_START',
        EXPECTED_BREAK_STOP: 'EXPECTED_STOP',
      },
      workingStatus: {
        WORKING: 'WORKING',
        FINISHED: 'FINISHED',
        ON_BREAK: 'ON_BREAK',
      },
      magnificationFactor: 2,
    };
  },

  mounted() {
    this.scrollCardToEarliestStart();
  },

  methods: {
    getCardRef() {
      return `${this.timePair.id}_ws_card`;
    },

    getTimeMarkers(spanningDays) {
      const markersInDay = 24;
      const totalMarkers = markersInDay * spanningDays;
      const timeFormat = DateTimeUtil.is24HourTimeUsed() ? 'HH:mm' : 'h a';

      let timeIterator = startOfDay(new Date());

      let markers = [];
      for (let i = 0; i < totalMarkers; i++) {
        markers.push(format(timeIterator, timeFormat));
        timeIterator = addHours(timeIterator, 1);
      }
      times(this.getTimeMarkerReductionFactor(), () => {
        markers = filter(markers, (marker, i) => i % 2 === 0);
        return markers;
      });
      return markers;
    },

    getTimeMarkerReductionFactor() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
        case 'md': {
          return 2;
        }
        case 'lg':
        case 'xl':
        default: {
          return 0;
        }
      }
    },

    isZoomed() {
      return this.magnificationFactor === 2;
    },

    async handleZoomClick() {
      this.changeMagnificationFactor();
      Vue.nextTick(() => this.scrollCardToEarliestStart());
    },

    changeMagnificationFactor() {
      this.magnificationFactor = (this.magnificationFactor === 1) ? 2 : 1;
    },

    scrollCardToEarliestStart() {
      const workScheduleCard = this.$refs[this.getCardRef()];

      const workScheduleBarPixelWidth = workScheduleCard.$el.clientWidth;

      const actualStartTime = this.timePair.workSchedule.actual.startTime;
      const expectedStartTime = this.timePair.workSchedule.expected.startTime;

      const scrollToTime = isBefore(actualStartTime, expectedStartTime) ? actualStartTime : expectedStartTime;

      const minutesToScroll = differenceInMinutes(scrollToTime, startOfDay(scrollToTime));

      const totalMinutes = this.timePair.spanningDays * constants.WorkSchedule.MINUTES_IN_DAY;

      const paddingPixels = 10;

      workScheduleCard.$el.scrollLeft = (minutesToScroll / totalMinutes) * this.magnificationFactor
        * this.timePair.spanningDays * workScheduleBarPixelWidth - paddingPixels;
    },
  },
};
</script>

<style scoped>

</style>
